import React, { useState, useEffect } from "react";
import axios from "axios";
import { X } from "lucide-react";

const baseUrl =
  process.env.REACT_APP_PUBLIC_API_URL 

  
const ResponseModal = ({ isOpen, onClose, data }) => {
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(false);
  const registrationId = data?.id ?? data?.registrationId;

  useEffect(() => {
    if (!isOpen || !registrationId) {
      setRegistration(null);
      return;
    }
    setLoading(true);
    setRegistration(null);
    axios
      .get(`${baseUrl}/${registrationId}`)
      .then((response) => {
        const res = response.data;
        if (res.success && res.registration) {
          setRegistration(res.registration);
        }
      })
      .catch((error) => {
        console.error("Error fetching registration:", error);
      })
      .finally(() => setLoading(false));
  }, [isOpen, registrationId]);

  if (!isOpen) return null;

  const r = registration;
  const display = (v) => (v != null && String(v).trim() !== "" ? v : "—");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-gray-200 rounded-[2rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 bg-white p-8 border-b border-gray-50 flex justify-between items-start z-10">
          <div className="w-full">
            <h2 className="text-2xl font-bold text-gray-800">
              Response Details – {display(registrationId)}
            </h2>
            {loading ? (
              <p className="text-sm text-gray-500 mt-4">Loading...</p>
            ) : r ? (
              <div className="flex flex-wrap gap-8 mt-6">
                <DetailHeaderItem
                  label="Full Name"
                  value={display(r.fullName)}
                />
                <DetailHeaderItem
                  label="Age"
                  value={r.age ? `${r.age} years` : "—"}
                />
                <DetailHeaderItem label="Gender" value={display(r.gender)} />
                <DetailHeaderItem label="Region" value={display(r.region)} />
                <DetailHeaderItem
                  label="Value Chain Interest"
                  value={display(r.valueChainInterest)}
                />
              </div>
            ) : !loading ? (
              <p className="text-sm text-gray-500 mt-4">Registration not found.</p>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {r && (
          <div className="p-8 space-y-6 bg-gray-50/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoSection title="Contact Information">
                <InfoItem label="Phone" value={display(r.phone)} />
                <InfoItem label="Email" value={display(r.email)} />
                <InfoItem label="WhatsApp" value={display(r.whatsapp)} />
                <InfoItem label="Disability" value={display(r.disability)} />
                <InfoItem label="District" value={display(r.district)} />
                <InfoItem label="Community" value={display(r.community)} />
              </InfoSection>

              <InfoSection title="Agriculture Interest">
                <InfoItem
                  label="Interest in Agriculture"
                  value={display(r.interestInAgriculture)}
                />
                <InfoItem
                  label="Value Chain Interest"
                  value={display(r.valueChainInterest)}
                />
                <InfoItem
                  label="Current Engagement"
                  value={display(r.currentEngagement)}
                />
              </InfoSection>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoSection title="Education & Occupation">
                <InfoItem
                  label="Highest Education"
                  value={display(r.highestEducation)}
                />
                <InfoItem
                  label="Enrollment Status"
                  value={display(r.enrollmentStatus)}
                />
                <InfoItem
                  label="Completion Status"
                  value={display(r.completionStatus)}
                />
                <InfoItem
                  label="Main Occupation"
                  value={display(r.mainOccupation)}
                />
              </InfoSection>

              <InfoSection title="Access to Resources">
                <InfoItem label="Land Access" value={display(r.landAccess)} />
                <InfoItem label="Finance Access" value={display(r.financeAccess)} />
                <InfoItem
                  label="Tools / Technology Access"
                  value={display(r.toolsTechnologyAccess)}
                />
                <InfoItem
                  label="Mobile Phone Access"
                  value={display(r.mobilephoneAccess)}
                />
              </InfoSection>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
              <InfoSection title="Safeguarding">
                <InfoItem
                  label="Safety Perception"
                  value={display(r.safetyPerception)}
                />
                <InfoItem label="Risk / Harmful" value={display(r.riskHarmful)} />
                <InfoItem label="Report Issues" value={display(r.reportIssues)} />
                <InfoItem
                  label="Safeguarding Info"
                  value={display(r.safeguardinginfo)}
                />
              </InfoSection>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailHeaderItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1">
      {label}
    </p>
    <p className="text-sm font-bold text-gray-900">{value}</p>
  </div>
);

const InfoSection = ({ title, children }) => (
  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
    <h3 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b border-gray-50">
      {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const InfoItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-500 font-medium">
      {label}: <span className="text-gray-700 font-semibold">{value}</span>
    </span>
  </div>
);

export default ResponseModal;
